import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import {
  FinancialMarginFacts,
  FinancialReconciliationSummary,
} from '../../../financial-reconciliation/models/financial-reconciliation.model';
import { PALETTE, splitPer100 } from '../../utils/dashboard-charts';
import { costCovered, marginUnavailablePercent } from '../../utils/dashboard-actions';

interface Step {
  label: string;
  value: number;
  color: string;
  total?: boolean;
  hint?: string;
}

interface Slice {
  label: string;
  /** Reais por R$ 100 de venda (a lista inteira soma R$ 100,00). */
  value: number;
  color: string;
}

const pt = (value: number, digits = 1): string =>
  value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: digits });

/**
 * Resultado do período (pedidos com nota emitida), em cascata: venda → impostos → tarifas do canal →
 * frete pago pela loja → antes do custo → custo das peças → contribuição. A cascata só chega à
 * contribuição com a MESMA regra do card Contribuição (`costCovered`: custo em 80% ou mais das
 * vendas); abaixo disso para em "Antes do custo" e diz o custo máximo para não ter prejuízo (valor
 * limpo com custo faltando seria uma margem que não existe). Margem indisponível (notas sem pedido
 * vinculado) também vale aqui e troca a cascata pela mensagem.
 * Os números vêm do resumo da conciliação (mesma regra da tela), no período e canal do painel.
 */
@Component({
  selector: 'app-dashboard-result',
  standalone: true,
  imports: [CommonModule, RouterModule, NgbTooltipModule],
  host: { class: 'col-12 col-xl-8' },
  styles: [
    `
      .result-row {
        display: grid;
        /* Valor e % numa linha só: cabe "R$ 1.263.094,00 100,0%" sem quebrar. */
        grid-template-columns: 11rem minmax(0, 1fr) 11rem;
        align-items: center;
        gap: 0.75rem;
        padding: 0.3rem 0;
      }

      .result-row--total {
        border-top: 1px solid var(--bs-border-color-translucent, rgba(67, 89, 113, 0.1));
        padding-top: 0.5rem;
        margin-top: 0.2rem;
      }

      .result-label {
        font-size: 0.8125rem;
        color: var(--bs-body-color, #566a7f);
      }

      .result-row--total .result-label,
      .result-row--total .result-value {
        font-weight: 700;
        color: var(--bs-heading-color, #384551);
      }

      .result-track {
        height: 0.5rem;
        border-radius: 1rem;
        background: var(--bs-border-color-translucent, rgba(67, 89, 113, 0.08));
        overflow: hidden;
      }

      .result-bar {
        height: 100%;
        border-radius: 1rem;
        transition: width 0.4s ease;
      }

      .result-value {
        text-align: right;
        font-size: 0.8125rem;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
      }

      .result-pct {
        display: inline-block;
        min-width: 3.25rem;
        color: var(--bs-secondary-color, #a1acb8);
        font-weight: 400;
        font-size: 0.75rem;
        margin-left: 0.25rem;
      }

      /* Celular: a cascata de 3 colunas vira "Para onde vai cada R$ 100" (barra única + lista). */
      .per100-title {
        font-size: 0.8125rem;
        font-weight: 600;
        margin-bottom: 0.6rem;
      }

      .per100-bar {
        display: flex;
        height: 0.75rem;
        border-radius: 1rem;
        overflow: hidden;
        gap: 2px;
        background: var(--bs-border-color-translucent, rgba(67, 89, 113, 0.08));
      }

      .per100-seg {
        min-width: 0;
      }

      .per100-list {
        list-style: none;
        margin: 0.75rem 0 0;
        padding: 0;
      }

      .per100-item {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.25rem 0;
        font-size: 0.8125rem;
        min-width: 0;
      }

      .per100-dot {
        flex: 0 0 auto;
        width: 0.5rem;
        height: 0.5rem;
        border-radius: 50%;
      }

      .per100-name {
        flex: 1 1 auto;
        min-width: 0;
        overflow-wrap: anywhere;
        color: var(--bs-body-color, #566a7f);
      }

      .per100-value {
        flex: 0 0 auto;
        font-variant-numeric: tabular-nums;
        font-weight: 600;
        color: var(--bs-heading-color, #384551);
      }
    `,
  ],
  template: `
    <div class="card h-100" data-result>
      <div class="card-header d-flex flex-wrap align-items-start justify-content-between gap-2">
        <div class="min-w-0">
          <h5 class="card-title mb-0">Resultado do período</h5>
          @if (result) {
            <small class="text-muted" data-result-base>{{ basisOrders }} {{ basisOrders === 1 ? 'pedido' : 'pedidos' }} com nota emitida</small>
          }
        </div>
        <a class="btn btn-sm btn-icon btn-text-secondary rounded-circle" routerLink="/conciliacao-financeira"
          [queryParams]="link" ngbTooltip="Ver conciliação financeira" container="body" aria-label="Ver conciliação financeira">
          <i class="ti ti-arrow-up-right"></i>
        </a>
      </div>
      <div class="card-body pt-1">
        @if (!result) {
          <div class="placeholder-glow">
            @for (i of [1, 2, 3, 4, 5]; track i) {
              <span class="placeholder rounded col-12 d-block mb-3" style="height: 0.9rem"></span>
            }
          </div>
        } @else if (unavailablePercent !== null) {
          <p class="text-muted small mb-0 py-4 text-center" data-result-unavailable>
            Margem indisponível: {{ unavailablePercent }}% das notas sem pedido vinculado
          </p>
        } @else if (base.netSales <= 0) {
          <p class="text-muted small mb-0 py-4 text-center">Nenhuma venda com nota emitida no período.</p>
        } @else {
          <div class="d-none d-md-block" data-result-cascade>
            @for (step of steps; track step.label) {
              <div class="result-row" [class.result-row--total]="step.total" [ngbTooltip]="step.hint || null" container="body">
                <span class="result-label">{{ step.label }}</span>
                <div class="result-track">
                  <div class="result-bar" [style.width.%]="width(step.value)" [style.background]="step.color"></div>
                </div>
                <span class="result-value">
                  {{ step.value | currency: 'BRL' }}<span class="result-pct">{{ pct(step.value) }}</span>
                </span>
              </div>
            }
          </div>

          <div class="d-md-none" data-result-per100>
            <h6 class="per100-title">Para onde vai cada R$ 100</h6>
            <div class="per100-bar" aria-hidden="true">
              @for (slice of barSlices; track slice.label) {
                <div class="per100-seg" [style.flex]="slice.value + ' 1 0'" [style.background]="slice.color"></div>
              }
            </div>
            <ul class="per100-list">
              @for (slice of slices; track slice.label) {
                <li class="per100-item" data-per100-item>
                  <span class="per100-dot" [style.background]="slice.color"></span>
                  <span class="per100-name">{{ slice.label }}</span>
                  <span class="per100-value">{{ slice.value | currency: 'BRL' }}</span>
                </li>
              }
            </ul>
          </div>

          @if (costed) {
            @if (coveragePercent < 100) {
              <small class="text-muted d-block mt-2" data-result-coverage>
                Custo informado em {{ pt(coveragePercent) }}% das vendas. O resultado considera só essas vendas.
              </small>
            }
          } @else {
            <div class="d-flex align-items-start gap-2 mt-3 p-2 rounded bg-label-warning" data-result-nocost>
              <i class="ti ti-info-circle mt-1"></i>
              <small>
                @if (coveragePercent > 0) {
                  Custo informado em {{ pt(coveragePercent) }}% das vendas.
                } @else {
                  Custo das peças não informado.
                }
                @if (base.preCostResult > 0) {
                  <span class="d-block">Para não ter prejuízo, o custo das peças precisa ficar abaixo de <b>{{ pct(base.preCostResult).trim() }}</b> do preço de venda.</span>
                } @else {
                  <span class="d-block">Mesmo com custo zero, as vendas não cobrem impostos, tarifas e frete.</span>
                }
              </small>
            </div>
          }

          @if (base.discount > 0) {
            <small class="text-muted d-block mt-2" data-result-discount>
              Desconto concedido no período: {{ base.discount | currency: 'BRL' }} ({{ discountPercent }} da venda)
            </small>
          }
        }
      </div>
    </div>
  `,
})
export class DashboardResultComponent implements OnChanges {
  @Input() result?: FinancialReconciliationSummary;
  @Input() period?: { from?: string; to?: string };
  /** Faturamento do período (kpis.revenue): base da regra de margem indisponível. */
  @Input() revenue?: number | null;
  @Input() channelId?: number | null;
  readonly palette = PALETTE;
  readonly pt = pt;

  link: Record<string, string | number | undefined> = {};
  unavailablePercent: number | null = null;
  costed = false;
  coveragePercent = 0;
  basisOrders = 0;
  discountPercent = '';
  base!: FinancialMarginFacts;
  steps: Step[] = [];
  slices: Slice[] = [];
  /** Fatias com largura positiva (a contribuição em prejuízo não desenha segmento). */
  barSlices: Slice[] = [];

  ngOnChanges(): void {
    this.link = { ...this.period, ...(this.channelId != null ? { integrationId: this.channelId } : {}) };
    const r = this.result;
    if (!r) return;

    this.unavailablePercent = marginUnavailablePercent(r, this.revenue);
    // Mesma regra do card Contribuição: só chega à contribuição com custo em 80% ou mais das vendas.
    this.costed = costCovered(r);
    this.base = this.costed ? r.costedMargin : r.margin;
    this.coveragePercent = r.costCoveragePercent ?? 0;
    this.basisOrders = Math.max(0, (r.orders ?? 0) - (this.costed ? (r.ordersWithoutCost ?? 0) : 0));
    this.discountPercent = this.base.netSales > 0 ? `${pt((this.base.discount / this.base.netSales) * 100)}%` : '';
    this.steps = this.buildSteps();
    this.slices = this.buildSlices();
    this.barSlices = this.slices.filter((s) => s.value > 0);
  }

  private buildSteps(): Step[] {
    const m = this.base;
    const steps: Step[] = [
      { label: 'Venda', value: m.netSales, color: this.palette.primary, hint: 'Total dos itens das notas emitidas, menos descontos.' },
      { label: 'Impostos', value: m.taxes, color: this.palette.danger, hint: 'ICMS, DIFAL, PIS e COFINS destacados na nota.' },
      { label: 'Tarifas do canal', value: m.fees, color: this.palette.danger, hint: 'Comissão do canal e outras cobranças descontadas do repasse.' },
      { label: 'Frete pago pela loja', value: m.sellerFreight, color: this.palette.danger, hint: 'Frete pago pelo vendedor no Mercado Livre.' },
      { label: 'Antes do custo', value: m.preCostResult, color: this.palette.info, total: true, hint: 'Custo máximo para não ter prejuízo.' },
    ];
    if (this.costed) {
      steps.push(
        { label: 'Custo das peças', value: m.cost, color: this.palette.warning, hint: 'Custo médio na data da venda.' },
        { label: 'Contribuição', value: m.cleanValue, color: m.cleanValue >= 0 ? this.palette.success : this.palette.danger, total: true },
      );
    }
    return steps;
  }

  // "Para onde vai cada R$ 100": o que sobra de cada R$ 100 de venda depois de cada etapa. A última
  // fatia (contribuição ou antes do custo) fecha a conta; a soma é sempre R$ 100,00 (splitPer100).
  private buildSlices(): Slice[] {
    const m = this.base;
    const parts: { label: string; amount: number; color: string }[] = [
      { label: 'Impostos', amount: m.taxes, color: this.palette.danger },
      { label: 'Tarifas do canal', amount: m.fees, color: '#ff7a5c' },
      { label: 'Frete pago pela loja', amount: m.sellerFreight, color: '#ffa48f' },
    ];
    if (this.costed) {
      parts.push(
        { label: 'Custo das peças', amount: m.cost, color: this.palette.warning },
        { label: 'Contribuição', amount: m.cleanValue, color: m.cleanValue >= 0 ? this.palette.success : this.palette.danger },
      );
    } else {
      parts.push({ label: 'Antes do custo', amount: m.preCostResult, color: this.palette.info });
    }
    const values = splitPer100(parts.map((p) => p.amount), m.netSales);
    return parts.map((p, i) => ({ label: p.label, value: values[i], color: p.color }));
  }

  width(value: number): number {
    const base = this.base.netSales;
    return base > 0 ? Math.max(0, Math.min(100, (Math.abs(value) / base) * 100)) : 0;
  }

  pct(value: number): string {
    const base = this.base.netSales;
    if (base <= 0) return '';
    return ` ${((value / base) * 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
  }
}
